import React, {useEffect, useState} from "react";
import "./SpaControlPage.css";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch} from "../../redux/store";
import {fetchSpa} from "../../redux/actions/spa.actions";
import RootState from "../../redux/states/root.state";
import useSpaCommand, { isV1Spa } from "../../hooks/useSpaCommand";
import {commandKeys} from "../../mqttData/commands/commands.enum";
import {SpaLiveLabels} from "../../mqttData/live/spaLiveLabels.enums";
import {SpaSettingsLabels} from "../../mqttData/settings/spaSettingsLabels";
import { AmpereMeter } from "../../components/SpaControl/AmpereMeter";
import Header from "../../components/common/Header/Header";
import SpaBoyLogo from '../../assets/logos/SpaBoyLogo.png';
import ColdtubPalLogo from '../../assets/logos/coldtubpal.png';
import { isColdTub } from '../../utils/brand';
import LightsDialog from './LightsDialog';
import { SpaConstLabels } from '../../mqttData/consts/spaConstsLabels.enums';
import {
    AlertIcon,
    BoltLineIcon,
    CloseIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronUpIcon,
    FilterLineIcon,
    LeafIcon,
    OnzenLineIcon,
    OzoneLineIcon,
} from "../../components/common/Icons/Icons";
import { inputDebounce, settingsDebounce } from "./debounceTimes";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import useRoleCheck from "../../hooks/useRoleCheck";
import { getPreferredUnit, formatTemp } from "../../utils/temperature";
import {SpaErrorLabels} from "../../mqttData/errors/spaErrorLabels.enums";
import {ArcticLabels} from "../SpaErrors/components/arcticLabels.enums";
import {StonlyLinks} from "../SpaErrors/components/stonly.enums";
import { isAppError } from "../../services/mqtt/v1/v1Errors";
import { ActiveCycleStyleMap, ActiveCycleTextMap } from "../../mqttData/live/heatCycle.enums";

const delay = 1000;

// 'ERR2' → 'ERROR 2', as in the mobile app. Anything else is shown as-is.
const formatErrorTitle = (code: string) => {
    const match = /^ERR(\d+)$/i.exec(String(code || '').trim());
    return match ? `ERROR ${match[1]}` : String(code || '').toUpperCase();
};
const SpaControlPage = () => {
    // Configs:
    const {id} = useParams();
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    // SPA:
    const {canAccess:canViewAmpereGauge} = usePermissionCheck(["view_ampere_gauge"]);
    const isConnected = useSelector((state: RootState) => 
        state.spa.currentSpaId === id && state.spa.isCurrentSpaConnected
    );

    const isConnecting = useSelector((state: RootState) => 
        state.spa.currentSpaId === id && state.spa.isConnecting
    );

    // COLOR:
    const [rgb, setRgb] = useState({ r: 255, g: 255, b: 255 });
    const [brightness, setBrightness] = useState(123);
    const [rdtPattern, setRdtPattern] = useState(0);
    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    // Fetch SPA From the DB
    useEffect(() => {
        if (id != null) {
            dispatch(fetchSpa(id));
        }
    }, [dispatch, id]);

    // Spa data and settings:
    const {data:settingsData} = useSelector((state: RootState) => state.settings);
    const {data:liveData} = useSelector((state: RootState) => state.live);
    const constData = useSelector((state: RootState) => state.const.data);
    const {data:spaData, loading:spaLoading} = useSelector((state: RootState) => state.spa);

    // Live connection + command publisher (AWS IoT for V2 spas, legacy broker for V1 / Coldfire):
    const command = useSpaCommand(id ?? "");

    // Function to determine which image to show based on pump states
    const getSpaImageInfo = (): { imagePath: string | undefined; tubFolder: string; tierFolder: string } => {        
        const pump1 = Boolean(liveData[SpaLiveLabels.Pump1]);
        const pump2 = Boolean(liveData[SpaLiveLabels.Pump2]);
        const pump3 = Boolean(liveData[SpaLiveLabels.Pump3]);
        const sds = Boolean(liveData[SpaLiveLabels.SDS]);
        
        const tubModel = spaData.tub_model;
        
        // Build the image name based on which pumps are on
        let imageName = '';

        if (pump1) imageName += '1'; // Every tub has a pump 1

        if (tubModel) {
            // Handle tub models if the config doesnt match the tub model
            // ie. if config has sds but the model is set to a non-sds tub
            // P = 1 pump, S = 2 pumps, L & LS = 3 pumps
            if (pump2 && ['S', 'L', 'LS'].includes(tubModel.tier)) { // S, L, and LS have a pump 2
                imageName += (imageName ? '-' : '') + '2'; 
            }

            if (pump3 && ['L', 'LS'].includes(tubModel.tier) && !(['Cub','Yukon'].includes(tubModel.name) && tubModel.sds)) {  // L & LS have a pump 3 unless it's a cub or yukon sds tub
                imageName += (imageName ? '-' : '') + '3';
            }
            
            if (sds && tubModel.sds) {
                imageName += (imageName ? '-' : '') + 'sds';
            };

        } else {
            // Don't need to handle as we're defaulting to a legend-select-sds tub which is 3 pumps + sds
            if (pump2) imageName += (imageName? '-' : '') + '2';
            if (pump3) imageName += (imageName? '-' : '') + '3';
            if (sds) imageName += (imageName? '-' : '') + 'sds';
        }
        
        // If no pumps are on, show 'off' image
        if (!imageName) {
            imageName = 'off';
        }

        // Determine the tub model and tier from spa data
        let tubFolder = 'summit'; // default fallback
        let tierFolder = 'legend-select-sds'; // default fallback
        
        if (tubModel) {
            // Map tub model names to folder names
            const tubNameMap: { [key: string]: string } = {
                'Arctic Fox': 'arctic_fox',
                'Summit': 'summit',
                'Summit XL': 'summit_xl',
                'Tundra': 'tundra',
                'Kodiak': 'kodiak',
                'Klondiker': 'klondiker',
                'Yukon': 'yukon',
                'Cub': 'cub',
                'Okanagan': 'okanagan',
                'Ocean': 'ocean',
                'Columbia': 'columbia'
            };
            
            // Map tiers to folder names
            const tierMap: { [key: string]: string } = {
                'P': tubModel.sds ? 'prestige-sds' : 'prestige',
                'S': tubModel.sds ? 'signature-sds' : 'signature',
                'L': tubModel.sds ? 'legend-sds' : 'legend',
                'LS': tubModel.sds ? 'legend-select-sds' : 'legend-select'
            };
            
            const tubName = tubNameMap[tubModel.name] || 'summit';
            const tierName = tierMap[tubModel.tier] || 'legend-select-sds';
            
            tubFolder = tubName;
            tierFolder = tierName;
        }
        
        // Use a path that should work from the public folder
        // You may need to move the images to public/assets/images/Tubs/ for this to work
        const imagePath = `/assets/images/Tubs/${tubFolder}/${tierFolder}/${imageName}.png`;

        return { imagePath, tubFolder, tierFolder };
    };

    const pumps = [
        {
            number: 1,
            label: SpaLiveLabels.Pump1,
            command: commandKeys.P1Next,
            config: SpaSettingsLabels.cfgP1
        },
        {
            number: 2,
            label: SpaLiveLabels.Pump2,
            command: commandKeys.P2Next,
            config: SpaSettingsLabels.cfgP2
        },
        {
            number: 3,
            label: SpaLiveLabels.Pump3,
            command: commandKeys.P3Next,
            config: SpaSettingsLabels.cfgP3
        },
        {
            number: 4,
            label: SpaLiveLabels.Pump4,
            command: commandKeys.P4Next,
            config: SpaSettingsLabels.cfgP4
        },
        {
            number: 5,
            label: SpaLiveLabels.Pump5,
            command: commandKeys.P5Next,
            config: SpaSettingsLabels.cfgP5
        }
    ];

    // Air blowers. Gated on the config flags like the pumps; the legacy apps only offer on/off.
    const blowers = [
        {
            number: 1,
            label: SpaLiveLabels.BL1,
            command: commandKeys.Bl1Next,
            config: SpaSettingsLabels.cfgB1
        },
        {
            number: 2,
            label: SpaLiveLabels.BL2,
            command: commandKeys.BL2Next,
            config: SpaSettingsLabels.cfgB2
        }
    ];

    // Handle temperature change with debouncing:
    const [temp, setTemp] = useState<number>(settingsData.TSP as number || 0);
    const [lastUserTemp, setLastUserTemp] = useState<number | null>(null);
    const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
    
    const [lastUserRGB, setLastUserRGB] = useState<{r: number, g: number, b: number} | null>(null);
    const [lastUserBrightness, setLastUserBrightness] = useState<number | null>(null);
    const [lastUserRdtPattern, setLastUserRdtPattern] = useState<number | null>(null);
    const [lastUpdateTimeRGB, setLastUpdateTimeRGB] = useState<number>(0);
    const [lastUpdateTimeBrightness, setLastUpdateTimeBrightness] = useState<number>(0);
    const [lastUpdateTimeRdtPattern, setLastUpdateTimeRdtPattern] = useState<number>(0);
    // Note: lastUserRGB, lastUserBrightness, and lastUserRdtPattern are used for syncing state from settings updates
    
    // Add new state for button hold and confirmation
    const [isHoldingUp, setIsHoldingUp] = useState(false);
    const [showTempWarning, setShowTempWarning] = useState(false);
    const [pendingTemp, setPendingTemp] = useState<number | null>(null);
    const [hasAcknowledgedWarning, setHasAcknowledgedWarning] = useState(false);
    const [holdTimer, setHoldTimer] = useState<NodeJS.Timeout | null>(null);
    
    // Effect to handle recieving setting updates
    useEffect(() => {
        if (settingsData.TSP !== undefined) {
            const now = Date.now();
            const settingsTemp = settingsData.TSP as number;
            
            if (
                now - lastUpdateTime > settingsDebounce || 
                (lastUserTemp !== null && settingsTemp === lastUserTemp)
            ) {
                setTemp(settingsTemp);
                setLastUserTemp(null); // Reset user temp after confirmation
            }
        }
    }, [settingsData.TSP]);

    useEffect(() => {
        if (settingsData[SpaSettingsLabels.RDT_red] !== undefined || settingsData[SpaSettingsLabels.RDT_green] !== undefined || settingsData[SpaSettingsLabels.RDT_blue] !== undefined) {
            const now = Date.now();
            const settingsRed = settingsData[SpaSettingsLabels.RDT_red] as number;
            const settingsGreen = settingsData[SpaSettingsLabels.RDT_green] as number;
            const settingsBlue = settingsData[SpaSettingsLabels.RDT_blue] as number;
            
            if (
                now - lastUpdateTimeRGB > settingsDebounce || 
                (lastUserRGB !== null && settingsRed === lastUserRGB.r && settingsGreen === lastUserRGB.g && settingsBlue === lastUserRGB.b)
            ) {
                setRgb({r: settingsRed, g: settingsGreen, b: settingsBlue});
                setLastUserRGB(null); 
            }
        }
    }, [settingsData[SpaSettingsLabels.RDT_red], settingsData[SpaSettingsLabels.RDT_green], settingsData[SpaSettingsLabels.RDT_blue]]);

    useEffect(() => {
        if (settingsData[SpaSettingsLabels.RDT_bright] !== undefined) {
            const now = Date.now();
            const settingsBrightness = settingsData[SpaSettingsLabels.RDT_bright] as number;

            if (
                now - lastUpdateTimeBrightness > settingsDebounce || 
                (lastUserBrightness !== null && settingsBrightness === lastUserBrightness)
            ) {
                setBrightness(settingsBrightness);
                setLastUserBrightness(null);
            }
        }
    }, [settingsData[SpaSettingsLabels.RDT_bright]]);

    useEffect(() => {
        if (settingsData[SpaSettingsLabels.RDT_pattern] !== undefined) {
            const now = Date.now();
            const settingsPattern = settingsData[SpaSettingsLabels.RDT_pattern] as number;

            if (
                now - lastUpdateTimeRdtPattern > settingsDebounce || 
                (lastUserRdtPattern !== null && settingsPattern === lastUserRdtPattern)
            ) {
                setRdtPattern(settingsPattern);
                setLastUserRdtPattern(null);
            }
        }
    }, [settingsData[SpaSettingsLabels.RDT_pattern]]);

    // Effect to handle sending setting updates
    useEffect(() => {
        if (lastUserTemp !== null && temp === lastUserTemp) {
            const timer = setTimeout(() => {
                command.updateSettings({
                    setTSP: lastUserTemp,
                });
            }, inputDebounce);
            
            return () => clearTimeout(timer);
        }
    }, [lastUserTemp, temp]);

    // Note: RGB, brightness, and pattern commands are handled by LightsDialog component

    // Handle SettingFunctions
    const handleTempChange = (newTemp: number) => {
        if (newTemp > 104 && !hasAcknowledgedWarning) {
            return;
        }
        
        if (newTemp <= 107) {
            setTemp(newTemp);
            setLastUserTemp(newTemp);
            setLastUpdateTime(Date.now());
        }
    }

    
    // Other Functions
    const handleConfirmHighTemp = () => {
        if (pendingTemp !== null) {
            setTemp(pendingTemp);
            setHasAcknowledgedWarning(true);
        }
        setShowTempWarning(false);
        setPendingTemp(null);
    }

    const handleCancelHighTemp = () => {
        setShowTempWarning(false);
        setPendingTemp(null);
    }

    const handleMouseDown = () => {
        if (temp === 104 && !hasAcknowledgedWarning) {
            const timer = setTimeout(() => {
                setPendingTemp(105);
                setShowTempWarning(true);
            }, 5000); // 5 second delay
            setHoldTimer(timer);
        } else if (hasAcknowledgedWarning) {
            setIsHoldingUp(true);
        }
    };

    const handleMouseUp = () => {
        if (holdTimer) {
            clearTimeout(holdTimer);
            setHoldTimer(null);
        }
        setIsHoldingUp(false);
    };

    const handleMouseLeave = () => {
        if (holdTimer) {
            clearTimeout(holdTimer);
            setHoldTimer(null);
        }
        setIsHoldingUp(false);
    };

    // Reset the acknowledged state when temperature goes below 104
    useEffect(() => {
        if (temp < 104) {
            setHasAcknowledgedWarning(false);
        }
    }, [temp]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (holdTimer) {
                clearTimeout(holdTimer);
            }
        };
    }, [holdTimer]);

    const pumpLevel = (pumpLabel:string) => {
        let num = liveData[pumpLabel] as number;
        if (num>15){
            return `high`

        } else if (num>0) {
            return `low`

        } else {
            return `default`
        }
    }

    const getConvertedValue = (value:any, convert = 100) => {
        return value ? (value as number) / convert : 0;
    };

    const [startTime, setStartTime] = useState(0);
    const [timeoutID, setTimeoutID] = useState<any>(null);
    const hasRdtColours = settingsData[SpaSettingsLabels.RDT_red] !== undefined
        || settingsData[SpaSettingsLabels.RDT_green] !== undefined
        || settingsData[SpaSettingsLabels.RDT_blue] !== undefined;
    const hasBrightness = settingsData[SpaSettingsLabels.RDT_bright] !== undefined;
    const hasRdtPattern = settingsData[SpaSettingsLabels.RDT_pattern] !== undefined;
    // cfgLi: 0/false = RGB lights (colours, patterns, brightness), 1/true = standard lights (on/off only)
    const isStandardLights = Boolean(settingsData[SpaSettingsLabels.cfgLi]);
    const hasLightsModalSettings = !isStandardLights && (hasRdtColours || hasBrightness || hasRdtPattern);
    const handlePress = (e:any) => {
        setStartTime(+new Date());
        const timer = hasLightsModalSettings ? setTimeout(()=>setOpen(true), delay) : null;
        setTimeoutID(timer);
    }

    const handleRelease = () => {
        const endTime = +new Date();
        const time = (endTime - startTime);
        // Without the lights modal, any press just toggles the lights
        if (time<delay || !hasLightsModalSettings) {
            command.executeSingle(commandKeys.LightsNext);
            clearTimeout(timeoutID);

        }
    }

    // Add state for preferred unit
    const [preferredUnit, setPreferredUnit] = useState<'F' | 'C'>(getPreferredUnit());

    // Add effect to listen for unit preference changes
    useEffect(() => {
        const handleStorageChange = () => {
            setPreferredUnit(getPreferredUnit());
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Function to convert temperature for display
    const getDisplayTemp = (temp: number): string => {
        return formatTemp(temp, preferredUnit);
    };

    // Add error and status data selectors
    const errorsData = useSelector((state: RootState) => state.error.data);
    const {canAccess: canViewAllErrors} = usePermissionCheck(['view_all_spa_errors']);
    
    const filteredErrors = canViewAllErrors ? Object.values(SpaErrorLabels).filter(
        (code) => errorsData[code]
      ) : Object.values(SpaErrorLabels).filter(
        (code) => errorsData[code] && ArcticLabels[code] !== ""
      );
    // V1 / Coldfire faults arrive decoded (ER code, old-app title, video), highest
    // priority first; the banner leads with the top one rather than a count. Only the
    // faults the mobile app shows, as on the app's control screen.
    const v1Errors = useSelector((state: RootState) => state.error.v1).filter(isAppError);
    const isV1 = isV1Spa(spaData);
    const shownErrors: { key: string; title: string; body: string; link: string | null }[] = isV1
        ? v1Errors.map((e) => ({ key: e.code, title: e.code, body: e.label, link: e.link }))
        : filteredErrors.map((code) => ({
            key: code,
            title: formatErrorTitle(code),
            body: ArcticLabels[code],
            link: StonlyLinks[code] || null,
        }));
    const moreErrors = shownErrors.length - 1;
    const errorBannerTitle = shownErrors.length > 1 && !isV1 ? `${shownErrors.length} ERRORS` : shownErrors[0]?.title;
    const errorBannerDetail = [
        shownErrors[0]?.body,
        moreErrors > 0
            ? (isV1 ? `(and ${moreErrors} more error${moreErrors === 1 ? '' : 's'})` : `+${moreErrors} more`)
            : null,
    ].filter(Boolean).join(' ');

    // Errors: an amber banner under the status (only while there are faults) that
    // opens a dialog of error cards, as in the mobile app.
    const [errorsOpen, setErrorsOpen] = useState(false);

    useEffect(() => {
        if (!errorsOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setErrorsOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [errorsOpen]);

    // Close the dialog if the faults clear while it's open.
    useEffect(() => {
        if (shownErrors.length === 0) setErrorsOpen(false);
    }, [shownErrors.length]);

    const isOffline = !isConnected && !isConnecting;
    const heatCycle = Math.max(liveData[SpaLiveLabels.H1]??0, liveData[SpaLiveLabels.H2]??0);
    const statusClass = isConnecting ? 'idle'
        : !isConnected ? 'offline'
        : liveData[SpaLiveLabels.Econ] ? 'econ'
        : ActiveCycleStyleMap[heatCycle];
    const statusText = isConnecting ? 'Connecting…'
        : !isConnected ? 'Offline'
        : liveData[SpaLiveLabels.Econ] ? 'Economy'
        : ActiveCycleTextMap[heatCycle];
    const hasSanitation = settingsData[SpaSettingsLabels.cfgOn]||settingsData[SpaSettingsLabels.cfgOzP1]||settingsData[SpaSettingsLabels.cfgOzP2];
    const isOzone = settingsData[SpaSettingsLabels.cfgOzP1]||settingsData[SpaSettingsLabels.cfgOzP2];
    const imageInfo = getSpaImageInfo();
    const offImageSrc = `/assets/images/Tubs/${imageInfo.tubFolder}/${imageInfo.tierFolder}/off.png`;
    const toggleState = (on: boolean) => isConnected ? (on ? 'selected' : 'default') : 'offline';

    return (
        <div className="page controls-page">
            <Header withShopLink={true}/>
            {isConnecting || spaLoading ?
            <div className="controls-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div> :
            <main className={`controls-main ${isOffline ? 'offline' : ''}`}>
            {isOffline && <div className="page-notice">Spa is offline</div>}
            {shownErrors.length > 0 && (
                <button
                    key={`${shownErrors.length}:${shownErrors[0].key}`}
                    className='error-banner'
                    onClick={() => setErrorsOpen(true)}
                    aria-haspopup='dialog'
                >
                    <AlertIcon className='error-banner-icon'/>
                    <span className='error-banner-text'>
                        <strong>{errorBannerTitle}</strong>
                        <span className='error-banner-detail'>{errorBannerDetail}</span>
                    </span>
                    <ChevronRightIcon className='error-banner-chevron'/>
                </button>
            )}

            {showTempWarning && (
                <div className="modal-overlay">
                    <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="temp-warning-title">
                        <h2 className="modal-title" id="temp-warning-title">High Temperature Warning</h2>
                        <p className="modal-body">Setting the temperature above {getDisplayTemp(104)}&deg;{preferredUnit} may increase energy consumption and wear on your spa components. Are you sure you want to proceed?</p>
                        <div className="modal-actions">
                            <button className="btn" onClick={handleCancelHighTemp}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleConfirmHighTemp}>Accept</button>
                        </div>
                    </div>
                </div>
            )}

            {errorsOpen && (
                <div className='errors-dialog' role='dialog' aria-modal='true' aria-label='Spa errors'>
                    <div className='errors-scrim' onClick={() => setErrorsOpen(false)}/>
                    <button className='errors-close' aria-label='Close errors' onClick={() => setErrorsOpen(false)}>
                        <CloseIcon/>
                    </button>
                    <div className='errors-list'>
                        {shownErrors.map((err) =>
                            <div className='error-card' key={err.key}>
                                <AlertIcon className='error-card-icon'/>
                                <h3 className='error-card-title'>{err.title}</h3>
                                {err.body && <p className='error-card-body'>{err.body}</p>}
                                {err.link && (
                                    <a className='error-card-action' href={err.link} target='_blank' rel='noreferrer'>
                                        LEARN MORE
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className='controls-top'>
                {/* LEFT: temperature */}
                <section className='temperature' aria-label='Temperature'>
                    <button
                        className='temp-button'
                        id='up'
                        aria-label='Raise set temperature'
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => {
                            if (temp === 104 && !hasAcknowledgedWarning) {
                                return; // Prevent click from increasing temperature at 104
                            }
                            handleTempChange(temp + 1);
                        }}
                        disabled={temp >= 107 || !isConnected}
                    >
                        <ChevronUpIcon/>
                    </button>
                    <div className='temp-readout'>
                        <span className='temp-current'>
                            {getDisplayTemp(liveData[SpaLiveLabels.CurrentTemp] as number ?? 0)}
                            <span className='temp-unit'>&deg;{preferredUnit}</span>
                        </span>
                        <span className='temp-set'>
                            <span className='temp-label'>Set</span>
                            {getDisplayTemp(temp)}&deg;{preferredUnit}
                        </span>
                    </div>
                    <button
                        className='temp-button'
                        id='down'
                        aria-label='Lower set temperature'
                        onClick={() => handleTempChange(temp - 1)}
                        disabled={temp <= (constData[SpaConstLabels.setTSPmin] ?? 59) || !isConnected}
                    >
                        <ChevronDownIcon/>
                    </button>
                </section>

                {/* MIDDLE: spa image + status */}
                <section className='spa' aria-label='Spa'>
                    <div className='spa-image'>
                        <div className='image'>
                            {/* Background "off" image - always show */}
                            <img src={offImageSrc} alt="Spa Base"/>
                            {/* Overlay pump status image (only when connected and not "off") */}
                            {isConnected && imageInfo.imagePath && imageInfo.imagePath !== offImageSrc && (
                                <img key={imageInfo.imagePath} src={imageInfo.imagePath} alt="Spa Status" className="animated"/>
                            )}
                        </div>
                        <div className='overlay'>
                            <button id='Q1' aria-label='Pump 1' disabled={!settingsData[SpaSettingsLabels.cfgP1]||!isConnected} onClick={() => command.executeSingle(commandKeys.P1Next)}>
                                {settingsData[SpaSettingsLabels.cfgP1] && <div id='P1' className={`indicator ${isConnected ? pumpLevel('P1'):`offline`}`}></div>}
                            </button>
                            <button id='Q2' aria-label='Pump 2' disabled={!settingsData[SpaSettingsLabels.cfgP2]||!isConnected} onClick={() => command.executeSingle(commandKeys.P2Next)}>
                                {settingsData[SpaSettingsLabels.cfgP2] && <div id='P2' className={`indicator ${isConnected ? pumpLevel('P2'):`offline`}`}></div>}
                            </button>
                            <button id='Q3' aria-label='Pump 3' disabled={!settingsData[SpaSettingsLabels.cfgP3]||!isConnected} onClick={() => command.executeSingle(commandKeys.P3Next)}>
                                {settingsData[SpaSettingsLabels.cfgP3] && <div id='P3' className={`indicator ${isConnected ? pumpLevel('P3'):`offline`}`}></div>}
                            </button>
                            <button id='Q4'
                                aria-label={settingsData[SpaSettingsLabels.cfgP4] ? 'Pump 4' : settingsData[SpaSettingsLabels.cfgSDS] ? 'SDS' : 'Lights'}
                                disabled={!isConnected}
                                onClick={() => settingsData[SpaSettingsLabels.cfgP4] ? command.executeSingle(commandKeys.P4Next) : settingsData[SpaSettingsLabels.cfgSDS] ? command.executeSingle(commandKeys.SDSNext) : command.executeSingle(commandKeys.LightsNext)}
                            >
                                {settingsData[SpaSettingsLabels.cfgP4] ?
                                    <div id='P4' className={`indicator ${isConnected ? pumpLevel('P4'):`offline`}`}></div>
                                :
                                settingsData[SpaSettingsLabels.cfgSDS] ?
                                    <div id='SDS' className={`indicator ${toggleState(Boolean(liveData[SpaLiveLabels.SDS]))}`}></div>
                                :
                                    <div id='Lights' className={`indicator ${toggleState(Boolean(liveData[SpaLiveLabels.Lights]))}`}></div>
                                }
                            </button>
                        </div>
                    </div>

                    <div className='spa-status-row'>
                        <span className={`chip spa-status ${statusClass}`}>{statusText}</span>
                    </div>
                </section>

                {/* RIGHT: navigation */}
                <nav className='controls-nav' aria-label='Spa pages'>
                    {settingsData[SpaSettingsLabels.cfgSB] &&
                        <button
                            className='nav-card spaboy'
                            aria-label={isColdTub ? 'COLDTUB Pal' : 'SpaBoy'}
                            onClick={() => navigate(`/spa-control/${id}/${isColdTub ? 'coldtub-pal' : 'spaboy'}`)}
                        >
                            <img src={isColdTub ? ColdtubPalLogo : SpaBoyLogo} alt='' className='nav-sb-logo'/>
                        </button>
                    }

                    <button className='nav-card' aria-label='Filtration' onClick={()=>navigate(`/spa-control/${id}/filtration`)}>
                        <span className='nav-card-icon'><FilterLineIcon/></span>
                        <span className='nav-card-label'>Filtration</span>
                        <ChevronRightIcon className='nav-card-chevron'/>
                    </button>

                    <button className='nav-card' aria-label='Economy' onClick={() => navigate(`/spa-control/${id}/peak-management`)}>
                        <span className='nav-card-icon'><LeafIcon/></span>
                        <span className='nav-card-label'>Economy</span>
                        <ChevronRightIcon className='nav-card-chevron'/>
                    </button>

                    {hasSanitation &&
                        <button className='nav-card' aria-label='Sanitation' onClick={() => navigate(`/spa-control/${id}/sanitation`)}>
                            <span className='nav-card-icon'>{isOzone ? <OzoneLineIcon/> : <OnzenLineIcon/>}</span>
                            <span className='nav-card-label'>Sanitation</span>
                            <ChevronRightIcon className='nav-card-chevron'/>
                        </button>
                    }

                    {/* Power history comes from the V2 (AWS IoT / DynamoDB) pipeline; V1 spas have none. */}
                    {!isV1Spa(spaData) &&
                        <button className='nav-card' aria-label='Power' onClick={() => navigate(`/spa/${id}/power-consumption`)}>
                            <span className='nav-card-icon'><BoltLineIcon/></span>
                            <span className='nav-card-label'>Power</span>
                            <ChevronRightIcon className='nav-card-chevron'/>
                        </button>
                    }

                    {canViewAmpereGauge &&
                        <div className='controls-ampere'>
                            <AmpereMeter value={getConvertedValue(liveData[SpaLiveLabels.Current])} offline={!isConnected}/>
                        </div>
                    }
                </nav>
            </div>

            {/* BOTTOM: actions */}
            <div className='actions'>
                {pumps.map((pump:any)=>
                    settingsData[pump.config] &&
                    <button
                        key={pump.label}
                        className={`action-button ${isConnected ? pumpLevel(pump.label):`offline`}`}
                        id={pump.label}
                        aria-label={`Pump ${pump.number}`}
                        onClick={() => command.executeSingle(pump.command)}
                        disabled={!isConnected||!settingsData[pump.config]}
                    ></button>
                )}

                {/* Blowers: only spas whose config reports them (today that is V1 / Coldfire); V1 drives them on/off */}
                {blowers.map((blower) =>
                    settingsData[blower.config] &&
                    <button
                        key={blower.label}
                        className={`action-button ${toggleState(Boolean(liveData[blower.label]))}`}
                        id={blower.label}
                        aria-label={`Blower ${blower.number}`}
                        onClick={() => command.executeSingle(blower.command)}
                        disabled={!isConnected||!settingsData[blower.config]}
                    ></button>
                )}

                {settingsData[SpaSettingsLabels.cfgFG] && <button disabled={!settingsData[SpaSettingsLabels.cfgFG]||!isConnected}
                    className={`action-button ${toggleState(Boolean(liveData[SpaLiveLabels.Fogger]))}`}
                    id={`Fogger`}
                    aria-label='Fogger'
                    onClick={() => command.executeSingle(commandKeys.FgNext)}
                ></button>}
                {settingsData[SpaSettingsLabels.cfgSDS] && <button disabled={!settingsData[SpaSettingsLabels.cfgSDS]||!isConnected}
                    className={`action-button ${toggleState(Boolean(liveData[SpaLiveLabels.SDS]))}`}
                    id={`SDS`}
                    aria-label='SDS'
                    onClick={() => command.executeSingle(commandKeys.SDSNext)}
                ></button>}
                {settingsData[SpaSettingsLabels.cfgYESS] && <button disabled={!settingsData[SpaSettingsLabels.cfgYESS]||!isConnected}
                    className={`action-button ${toggleState(Boolean(liveData[SpaLiveLabels.Yess]))}`}
                    id={`YESS`}
                    aria-label='YESS'
                    onClick={() => command.executeSingle(commandKeys.YESSNext)}
                ></button>}
                <button
                    disabled={!isConnected}
                    className={`action-button ${toggleState(Boolean(liveData[SpaLiveLabels.Lights]))}`}
                    id={`Lights`}
                    aria-label={hasLightsModalSettings ? 'Lights (hold for colours)' : 'Lights'}
                    onMouseDown={(e)=>handlePress(e)}
                    onMouseUp={handleRelease}
                    onContextMenu={(e) => {
                        e.preventDefault(); // Prevents context menu
                        return false;
                    }}
                    onTouchStart={(e) => {
                        e.preventDefault(); // Prevents context menu and text selection
                        handlePress(e);
                    }}
                    onTouchEnd={(e) => {
                        e.preventDefault();
                        handleRelease();
                    }}
                ></button>
                <LightsDialog
                    open={open && hasLightsModalSettings}
                    onClose={handleClose}
                    command={command}
                    initialRgb={rgb}
                    initialBrightness={brightness}
                    initialPattern={rdtPattern}
                    showRdtColours={hasRdtColours}
                    showBrightness={hasBrightness}
                    showPatterns={hasRdtPattern}
                />
            </div>
        </main>}
    </div>

    );
};


export default SpaControlPage;
